type GoogleAdsPurchase = {
  transactionId: string;
  value: number;
  currency: string;
  email?: string | null;
};

const PURCHASE_DEDUP_PREFIX = 'google_ads_purchase:';
const configuredTags = new WeakMap<Function, Set<string>>();

function purchaseSendTo(): string | null {
  const value = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_SEND_TO?.trim();
  if (!value || !/^AW-\d+\/[A-Za-z0-9_-]+$/.test(value)) return null;
  return value;
}

function enhancedConversionsEnabled(): boolean {
  return process.env.REACT_APP_GOOGLE_ADS_ENHANCED_CONVERSIONS === 'true';
}

/** Configure Ads on arrival, while the landing URL still contains click IDs. */
export function initializeGoogleAds(): boolean {
  const sendTo = purchaseSendTo();
  const gtag = (window as any).gtag;
  if (!sendTo || typeof gtag !== 'function') return false;
  const conversionId = sendTo.split('/', 1)[0];
  const configured = configuredTags.get(gtag) || new Set<string>();
  if (configured.has(conversionId)) return true;
  try {
    gtag('config', conversionId);
    configured.add(conversionId);
    configuredTags.set(gtag, configured);
    return true;
  } catch {
    return false;
  }
}

/**
 * Queue one Google Ads Purchase after the backend has verified the invoice.
 * `transaction_id` lets Google deduplicate repeated callback-page visits; the
 * local marker avoids queuing the same event twice in one browser.
 */
export function trackGoogleAdsPurchase({
  transactionId,
  value,
  currency,
  email,
}: GoogleAdsPurchase): boolean {
  const sendTo = purchaseSendTo();
  const cleanTransactionId = String(transactionId || '').trim();
  const cleanCurrency = String(currency || '').trim().toUpperCase();
  const numericValue = Number(value);
  const gtag = (window as any).gtag;

  if (
    !sendTo ||
    !cleanTransactionId ||
    !cleanCurrency ||
    !Number.isFinite(numericValue) ||
    numericValue <= 0 ||
    typeof gtag !== 'function'
  ) {
    return false;
  }

  const dedupKey = `${PURCHASE_DEDUP_PREFIX}${cleanTransactionId}`;
  try {
    if (window.localStorage.getItem(dedupKey)) return false;
  } catch { }

  try {
    if (!initializeGoogleAds()) return false;

    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (enhancedConversionsEnabled() && normalizedEmail) {
      // Google tag normalizes and hashes eligible first-party user data before
      // transmission. Keep this feature off until consent/privacy review passes.
      gtag('set', 'user_data', { email: normalizedEmail });
    }

    gtag('event', 'conversion', {
      send_to: sendTo,
      value: numericValue,
      currency: cleanCurrency,
      transaction_id: cleanTransactionId,
    });
    try { window.localStorage.setItem(dedupKey, '1'); } catch { }
    return true;
  } catch {
    return false;
  }
}
